import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Check, GraduationCap, Globe2, Clock, Award, Users, BookOpen, ArrowRight, Sparkles } from "lucide-react";

const COGNIA_LOGO = "https://customer-assets.emergentagent.com/job_global-continuity/artifacts/xiv5benz_61af194567ecd52c38758fd7_Cognia%20glossy%20logo-p-500.png";
const STUDENT_IMG = "https://customer-assets.emergentagent.com/job_global-continuity/artifacts/sbwj184j_iStock-2148802894.jpg";
const GRAD_IMG = "https://images.pexels.com/photos/36622167/pexels-photo-36622167.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function ProgramsPage() {
  const { lang, t } = useLang();
  const isFR = lang === "fr";

  return (
    <div data-testid="programs-page" className="bg-paper">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16">
        <p className="overline mb-4">{isFR ? "Le parcours académique parallèle" : "The parallel academic pathway"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-4xl">
          {isFR ? "Un avenir international, sans quitter la France." : "Build an international future, without leaving France."}
        </h1>
        <p className="mt-6 max-w-2xl text-base lg:text-lg leading-relaxed text-muted-foreground">
          {isFR
            ? "OFA France propose un parcours académique américain structuré, en parallèle du système français, de la Grande Section à la Terminale — avec la possibilité d'obtenir un US High School Diploma accrédité COGNIA."
            : "OFA France offers a structured American academic pathway, parallel to the French system, from Kindergarten to 12th grade — with the option to earn a COGNIA-accredited US High School Diploma."}
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/register" data-testid="programs-cta-signup"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-7 shadow-md">{t("nav.signup")} <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
          <Link to="/inquiry" data-testid="programs-cta-inquiry"><Button size="lg" variant="outline" className="rounded-full px-7 border-2">{t("landing.ctaInquiry")}</Button></Link>
          <Link to="/recommender" data-testid="programs-cta-recommender">
            <Button size="lg" className="rounded-full px-6 border-2 border-accent bg-accent/10 text-accent hover:bg-accent hover:text-accent-foreground transition-colors">
              <Sparkles className="w-4 h-4 mr-2" />
              {isFR ? "Quelle voie ? · 2 min" : "Which track? · 2 min"}
            </Button>
          </Link>
        </div>

        {/* Stats strip */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-lg overflow-hidden border border-border">
          <Stat label={isFR ? "Élèves par classe" : "Students per class"} value="4–8" />
          <Stat label={isFR ? "Accréditation" : "Accreditation"} value="COGNIA" />
          <Stat label={isFR ? "Parcours continu" : "Uninterrupted pathway"} value="GS — Terminale" />
          <Stat label={isFR ? "Semaines de cours" : "Weeks of class"} value="32" />
        </div>
      </section>

      {/* Pathway recommender CTA strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 -mt-4 pb-8">
        <Link to="/recommender" data-testid="programs-recommender-cta" className="block group">
          <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 hover:border-primary hover:bg-primary/10 transition-all p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="overline mb-1 text-primary">{isFR ? "Pathway Recommender · 2 min" : "Pathway Recommender · 2 min"}</div>
              <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight">
                {isFR ? "Pas sûr de la voie qui correspond à votre enfant ?" : "Not sure which track fits your child?"}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">{isFR ? "Trois questions, et nous suggérons Fluent ou Bridge Track + le tarif estimé." : "Three questions, and we'll suggest Fluent or Bridge Track + estimated tuition."}</p>
            </div>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 shrink-0 group-hover:shadow-md transition-shadow">
              {isFR ? "Démarrer le quiz" : "Start the quiz"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </Link>
      </section>

      {/* The two tracks */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <p className="overline mb-3">{isFR ? "Deux voies possibles" : "Two possible tracks"}</p>
        <h2 className="font-serif text-3xl lg:text-4xl tracking-tight mb-12 max-w-2xl">
          {isFR ? "Choisir la voie qui correspond à votre enfant." : "Choose the track that fits your child."}
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card data-testid="track-fluent" className="overflow-hidden">
            <div className="bg-primary text-primary-foreground p-8">
              <Badge variant="outline" className="bg-accent text-accent-foreground border-0 mb-4">{isFR ? "Bilingue confirmé" : "Confirmed bilingual"}</Badge>
              <h3 className="font-serif text-3xl tracking-tight">Fluent Track</h3>
              <p className="mt-3 text-sm opacity-80">
                {isFR
                  ? "Pour les élèves déjà à l'aise en anglais. Programme académique américain complet."
                  : "For students already comfortable in English. Full American academic curriculum."}
              </p>
            </div>
            <CardContent className="p-8 space-y-3 text-sm">
              <Bullet>{isFR ? "Cours d'anglais académique : littérature, écriture, pensée critique" : "Academic English: literature, writing, critical thinking"}</Bullet>
              <Bullet>{isFR ? "Niveau natif visé en compréhension et production écrite" : "Native-level reading and writing target"}</Bullet>
              <Bullet>{isFR ? "Idéal pour familles expatriées de retour ou enfants déjà bilingues" : "Ideal for returning expat families or already-bilingual children"}</Bullet>
            </CardContent>
          </Card>

          <Card data-testid="track-bridge" className="overflow-hidden">
            <div className="bg-secondary p-8 border-b border-border">
              <Badge variant="outline" className="mb-4">{isFR ? "Vers le bilinguisme" : "Building bilingualism"}</Badge>
              <h3 className="font-serif text-3xl tracking-tight">Bridge Track</h3>
              <p className="mt-3 text-sm text-muted-foreground">
                {isFR
                  ? "Pour les élèves qui construisent leur anglais. Renforcement linguistique structuré."
                  : "For students building English. Structured linguistic reinforcement."}
              </p>
            </div>
            <CardContent className="p-8 space-y-3 text-sm">
              <Bullet>{isFR ? "Approche progressive : de la langue à l'académique en anglais" : "Progressive approach: from language to academic English"}</Bullet>
              <Bullet>{isFR ? "Soutien personnalisé en petites classes" : "Personalized support in small classes"}</Bullet>
              <Bullet>{isFR ? "Pont naturel vers le Fluent Track" : "Natural bridge toward the Fluent Track"}</Bullet>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Hours per level */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <p className="overline mb-3">{isFR ? "Volume horaire" : "Class hours"}</p>
        <h2 className="font-serif text-3xl lg:text-4xl tracking-tight mb-12 max-w-2xl">
          {isFR ? "Une présence régulière, chaque semaine." : "Steady weekly presence."}
        </h2>
        <div className="grid md:grid-cols-3 gap-px bg-border border border-border rounded-lg overflow-hidden">
          <LevelBlock
            level={isFR ? "Élémentaire — GS à CM2" : "Elementary — K to 5th"}
            hours="1h30 / sem."
            detail={isFR ? "2 séances de 45 min, chaque année. Bases bilingues solides." : "Two 45-min sessions per week, every year. Solid bilingual foundations."}
            price="1 995 € / cours / an"
          />
          <LevelBlock
            level={isFR ? "Collège — 6e à 3e" : "Middle School — 6th to 9th"}
            hours="1h30 / sem."
            detail={isFR ? "Anglais académique : écriture, littérature, pensée critique. Option : bloc de 1h30." : "Academic English: writing, literature, critical thinking. Option: 1h30 block."}
            price="1 995 € / cours / an"
            highlight
          />
          <LevelBlock
            level={isFR ? "Lycée — 2nde à Terminale" : "High School — 10th to 12th"}
            hours="1h30 + 1h30"
            detail={isFR ? "1h30 d'anglais + 1h30 d'American History (obligatoire 2 ans pour le diplôme)." : "1h30 English + 1h30 American History (2 years required for the diploma)."}
            price="1 995 € / cours / an"
          />
        </div>
        <p className="mt-6 text-sm text-muted-foreground italic">
          {isFR
            ? "Tarif indicatif pour le programme d'anglais. 32 semaines de cours par année. Petits effectifs (4 à 8 élèves)."
            : "Indicative pricing for the English program. 32 weeks of class per year. Small classes (4–8 students)."}
        </p>
      </section>

      {/* COGNIA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <Card className="overflow-hidden">
          <div className="grid md:grid-cols-5 items-center">
            <div className="md:col-span-3 p-10 lg:p-14">
              <p className="overline mb-3">{isFR ? "Reconnaissance internationale" : "International recognition"}</p>
              <h2 className="font-serif text-3xl lg:text-4xl tracking-tight mb-4">
                {isFR ? "Un High School Diploma accrédité COGNIA." : "A COGNIA-accredited High School Diploma."}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {isFR
                  ? "Notre diplôme américain est reconnu par les universités du monde entier. COGNIA est l'organisme d'accréditation de référence pour les écoles aux États-Unis et à l'international."
                  : "Our American diploma is recognized by universities worldwide. COGNIA is the leading accreditation body for schools in the US and internationally."}
              </p>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <Bullet><Award className="w-4 h-4 inline mr-1 text-accent" />{isFR ? "Diplôme reconnu mondialement" : "Globally recognized diploma"}</Bullet>
                <Bullet><Globe2 className="w-4 h-4 inline mr-1 text-accent" />{isFR ? "Accès facilité aux universités" : "Easier university access"}</Bullet>
                <Bullet><BookOpen className="w-4 h-4 inline mr-1 text-accent" />{isFR ? "American History incluse" : "American History included"}</Bullet>
                <Bullet><Users className="w-4 h-4 inline mr-1 text-accent" />{isFR ? "Orientation universitaire incluse" : "University guidance included"}</Bullet>
              </div>
            </div>
            <div className="md:col-span-2 bg-secondary/40 p-10 flex items-center justify-center min-h-[280px]">
              <img src={COGNIA_LOGO} alt="COGNIA accreditation" className="max-w-[220px] h-auto" />
            </div>
          </div>
        </Card>
      </section>

      {/* Why visual */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="overline mb-3">{isFR ? "L'avantage OFA France" : "The OFA France advantage"}</p>
          <h2 className="font-serif text-3xl lg:text-4xl tracking-tight mb-6">
            {isFR ? "Pas une école de langue. Une école." : "Not a language school. A school."}
          </h2>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3"><Clock className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} /><span><b>{isFR ? "Cohérence longue durée" : "Long-term coherence"}.</b> {isFR ? "Un parcours K-12 structuré, pas du soutien isolé." : "A structured K-12 pathway, not isolated tutoring."}</span></li>
            <li className="flex gap-3"><Users className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} /><span><b>{isFR ? "Petites classes (4–8)" : "Small classes (4–8)"}.</b> {isFR ? "Attention personnalisée, participation active." : "Personalized attention, real participation."}</span></li>
            <li className="flex gap-3"><GraduationCap className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} /><span><b>{isFR ? "Double système" : "Dual system"}.</b> {isFR ? "Pensé pour les élèves naviguant entre attentes françaises et américaines." : "Designed for students balancing French and American expectations."}</span></li>
            <li className="flex gap-3"><Globe2 className="w-5 h-5 text-accent shrink-0 mt-0.5" strokeWidth={1.5} /><span><b>{isFR ? "Professeurs natifs" : "Native teachers"}.</b> {isFR ? "Éducateurs anglophones experts en curriculum international." : "Native English educators experienced in international curricula."}</span></li>
          </ul>
        </div>
        <div className="rounded-lg overflow-hidden border border-border">
          <img src={STUDENT_IMG} alt="Confident student" className="w-full h-[420px] object-cover" />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 text-center">
        <p className="overline mb-3">{isFR ? "Premier contact" : "First contact"}</p>
        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight mb-4">
          {isFR ? "Prêt à construire l'avenir international de votre enfant ?" : "Ready to build your child's international future?"}
        </h2>
        <p className="text-muted-foreground max-w-xl mx-auto mb-8">
          {isFR ? "Une équipe humaine vous accompagne, en français ou en anglais." : "A human team supports you, in French or English."}
        </p>
        <div className="flex justify-center gap-3 flex-wrap">
          <Link to="/register"><Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-7 shadow-md" data-testid="programs-bottom-signup">{t("nav.signup")}</Button></Link>
          <Link to="/inquiry"><Button size="lg" variant="outline" className="rounded-full px-7 border-2" data-testid="programs-bottom-inquiry">{t("landing.ctaInquiry")}</Button></Link>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-card p-6 text-center">
      <div className="font-serif text-3xl tracking-tighter">{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <div className="flex items-start gap-2">
      <Check className="w-4 h-4 text-accent shrink-0 mt-1" strokeWidth={2} />
      <span>{children}</span>
    </div>
  );
}

function LevelBlock({ level, hours, detail, price, highlight }) {
  return (
    <div className={`p-8 ${highlight ? "bg-secondary/50" : "bg-card"}`}>
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{level}</div>
      <div className="font-serif text-3xl tracking-tighter">{hours}</div>
      <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{detail}</div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Tarif</div>
        <div className="font-serif text-2xl mt-1 text-accent">{price}</div>
      </div>
    </div>
  );
}
